import React from 'react';
import { Card, Space, Typography, Tag, Alert, Skeleton, Grid, theme } from 'antd';
import {
  CheckCircleFilled, CloseCircleFilled, QuestionCircleFilled, WarningFilled,
} from '@ant-design/icons';
import { POSITIVO, NEGATIVO } from '../../utils/opinionCumplimiento';
import { antiguedad, tieneMasDeUnMes } from '../../utils/pdfTexto';

const { Text, Title } = Typography;

/**
 * El veredicto de la opinión de cumplimiento, en grande.
 *
 * El dato que le importa al contribuyente es uno: si está al corriente o no.
 * Se distingue por color, icono Y encabezado —no solo por el texto— porque un
 * veredicto que hay que leer con atención para saber si es bueno o malo no
 * informa mejor que el PDF del que salió.
 *
 * Lo que NUNCA hace: pintar de rojo un fallo de lectura. No haber podido leer
 * el documento no es incumplir, y confundirlos sería asustar a quien está bien.
 */

const MOTIVOS = {
  discrepancia: 'El documento se contradice a sí mismo: el sentido impreso no coincide con su cadena original.',
  sin_sentido: 'No se encontró el sentido (POSITIVO o NEGATIVO) en el documento.',
  sin_texto: 'No se pudo extraer texto del documento.',
};

const SemaforoOpinion = ({ lectura, cargando, rfcEmpresa, hayOpinion }) => {
  const { token } = theme.useToken();
  const esMovil = !Grid.useBreakpoint().md;

  if (cargando) {
    return (
      <Card size="small">
        <Skeleton active paragraph={{ rows: 1 }} title={{ width: '40%' }} />
      </Card>
    );
  }

  // Sin opinión no hay semáforo de ningún color: no saber no es un estado
  // intermedio entre cumplir e incumplir.
  if (!hayOpinion) {
    return (
      <Alert
        type="info"
        showIcon
        message="Sin opinión de cumplimiento"
        description="Cuando tu gestor cargue tu opinión, aquí verás de un vistazo si estás al corriente con el SAT."
      />
    );
  }

  if (!lectura || (lectura.sentido !== POSITIVO && lectura.sentido !== NEGATIVO)) {
    return (
      <Alert
        type="warning"
        showIcon
        icon={<QuestionCircleFilled />}
        message="No se pudo interpretar la opinión"
        description={
          <Space direction="vertical" size={2}>
            <Text>{MOTIVOS[lectura?.motivo] || 'El documento no se pudo leer.'}</Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Esto no significa que estés mal con el SAT: significa que no se pudo leer el
              archivo. Descárgalo para revisarlo tú.
            </Text>
          </Space>
        }
      />
    );
  }

  const positivo = lectura.sentido === POSITIVO;
  const color = positivo ? token.colorSuccess : token.colorError;
  const fondo = positivo ? token.colorSuccessBg : token.colorErrorBg;
  const borde = positivo ? token.colorSuccessBorder : token.colorErrorBorder;

  const rfcDistinto = lectura.rfc && rfcEmpresa && lectura.rfc !== rfcEmpresa;
  const edad = antiguedad(lectura.emitidaEn);
  const vieja = tieneMasDeUnMes(lectura.emitidaEn);

  const dato = (etiqueta, valor, extra) => (
    <Space direction="vertical" size={0}>
      <Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {etiqueta}
      </Text>
      <Text style={{ fontSize: 13 }}>
        {valor}
        {extra}
      </Text>
    </Space>
  );

  return (
    <Space direction="vertical" size="small" style={{ width: '100%' }}>
      <Card
        size="small"
        styles={{ body: { padding: esMovil ? 16 : 20 } }}
        style={{ background: fondo, borderColor: borde }}
      >
        <Space
          direction={esMovil ? 'vertical' : 'horizontal'}
          align={esMovil ? 'start' : 'center'}
          size={esMovil ? 12 : 20}
          style={{ width: '100%' }}
        >
          {positivo
            ? <CheckCircleFilled style={{ fontSize: esMovil ? 36 : 46, color }} />
            : <CloseCircleFilled style={{ fontSize: esMovil ? 36 : 46, color }} />}

          <Space direction="vertical" size={2}>
            <Title level={esMovil ? 5 : 4} style={{ margin: 0, color }}>
              {positivo ? 'Al corriente con el SAT' : 'No estás al corriente con el SAT'}
            </Title>
            <Text style={{ fontSize: 13 }}>
              {positivo
                ? 'Tu opinión de cumplimiento es positiva.'
                : 'Tu opinión de cumplimiento es negativa: hay obligaciones pendientes.'}
            </Text>
          </Space>
        </Space>

        {(lectura.rfc || lectura.folio || lectura.emitidaEn) && (
          <Space
            wrap
            size={esMovil ? 'middle' : 'large'}
            style={{ marginTop: 16, paddingTop: 12, borderTop: `1px solid ${borde}` }}
          >
            {lectura.rfc && dato(
              'RFC del documento',
              lectura.rfc,
              rfcEmpresa && !rfcDistinto
                ? <Tag color="green" style={{ marginLeft: 8 }}>coincide</Tag>
                : null
            )}
            {lectura.folio && dato('Folio', lectura.folio)}
            {lectura.emitidaEn && dato(
              'Emitida',
              lectura.emitidaEn.toLocaleDateString('es-MX', {
                day: 'numeric', month: 'short', year: 'numeric',
              }),
              edad ? <Text type="secondary" style={{ fontSize: 12 }}> · {edad}</Text> : null
            )}
            {lectura.origen === 'ocr' && dato('Lectura', 'reconocimiento de texto (OCR)')}
          </Space>
        )}
      </Card>

      {/* La antigüedad NO cambia el veredicto ni el color del semáforo: una
          opinión positiva de hace seis meses sigue diciendo que el
          contribuyente estaba al corriente. Lo que hace falta saber es que
          conviene pedir una nueva, no que esta sea inválida — el SAT no imprime
          caducidad en el documento y el sistema no va a inventarla. */}
      {vieja && (
        <Alert
          type="warning"
          showIcon
          message={`Esta opinión se emitió ${edad}`}
          description="Refleja la situación del contribuyente en esa fecha, no necesariamente la de hoy. Conviene pedir una actualizada antes de usarla ante un tercero."
        />
      )}

      {/* La advertencia acompaña al veredicto en vez de sustituirlo: quien mira
          tiene que poder ver que lo que está leyendo no es suyo. */}
      {rfcDistinto && (
        <Alert
          type="error"
          showIcon
          icon={<WarningFilled />}
          message="Este documento es de otro RFC"
          description={`La opinión cargada corresponde a ${lectura.rfc}, no a ${rfcEmpresa}. El resultado de arriba no es de este contribuyente: hay que reemplazarla por la correcta.`}
        />
      )}
    </Space>
  );
};

export default SemaforoOpinion;
